import React, { Component, ErrorInfo, ReactNode } from "react";
import { TokenService } from "../services/token.service";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console (in production, you'd send this to an error reporting service)
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to send error to error reporting service:
    // errorReportingService.logError(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoDashboard = () => {
    window.location.href = "/dashboard";
  };

  render() {
    if (this.state.hasError) {
      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise, show default error UI
      const isDevelopment = import.meta.env.DEV;
      const showDetails =
        this.props.showDetails !== undefined
          ? this.props.showDetails
          : isDevelopment;
      const hasAppSession = TokenService.isInternalTokenValid();

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h1 className="error-boundary-title">Oops something went wrong.</h1>
            <p className="error-boundary-message">
              We're sorry, but something unexpected happened. Please try
              refreshing the page or contact support if the problem persists.
            </p>

            {showDetails && this.state.error && (
              <details className="error-boundary-details">
                <summary className="error-boundary-details-summary">
                  Error Details
                </summary>
                <div className="error-boundary-details-content">
                  <p className="error-boundary-error-name">
                    {this.state.error.name}
                  </p>
                  <p className="error-boundary-error-message">
                    {this.state.error.message}
                  </p>
                  {this.state.error.stack && (
                    <pre className="error-boundary-stack">
                      {this.state.error.stack}
                    </pre>
                  )}
                  {this.state.errorInfo && (
                    <pre className="error-boundary-stack">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <div className="error-boundary-actions">
              <button
                className="btn btn-primary"
                onClick={this.handleReset}
                aria-label="Try again to reload the application"
              >
                Try Again
              </button>
              {hasAppSession && (
                <button
                  className="btn btn-secondary"
                  onClick={this.handleGoDashboard}
                  aria-label="Go to dashboard"
                >
                  Go to Dashboard
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={this.handleReload}
                aria-label="Refresh the page"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
